import type { SimEvent } from '@prism/sim';
import { AudioBus, SilentSink } from './audio.js';
import { DamageNumbers, type DamageNumberMode } from './damageNumbers.js';
import { ParticleSystem } from './particles.js';
import { PRESETS, type PresetName } from './presets.js';
import { Screenshake, TRAUMA } from './shake.js';

/**
 * The VFX orchestrator (TRD-04 §2).
 *
 * ── The one architectural rule ────────────────────────────────────────────────────────
 * This class is driven ENTIRELY by the `SimEvent` stream and real elapsed time. It never
 * reads world state, never writes to it, and never influences determinism (R-04-01).
 *
 * That is testable, and it is tested: `AC-1` runs a full world twice — once with this
 * system consuming every event, once with it absent — and asserts the run hash is
 * bit-identical. If dropping every VFX call changed the sim, presentation would be
 * gameplay and every daily submission would be a coin flip.
 *
 * It follows that events must carry every field needed to draw them. Reaching back into
 * the world for "where did that happen" is how that coupling creeps in.
 */

export interface HitstopRequest {
  ms: number;
}

/** Freeze durations (R-04-12). Frozen frames are what make an impact read as heavy. */
const HITSTOP = {
  crit: 40,
  bossKill: 220,
  coreDamaged: 90,
  synergyTier6: 60,
} as const;

export interface VfxSettings {
  screenshakeScale: number;
  damageNumbers: DamageNumberMode;
  hitstopEnabled: boolean;
  showDamageFlash: boolean;
}

export const DEFAULT_VFX_SETTINGS: VfxSettings = {
  screenshakeScale: 1,
  damageNumbers: 'all',
  hitstopEnabled: true,
  showDamageFlash: true,
};

/**
 * The colour a status wears, so one `statusPuff` preset serves all of them.
 *
 * Matching the status pips the renderer draws — a green puff and a green pip are the
 * same information twice, which is how a player learns what the pip means without a
 * tooltip.
 */
const STATUS_TINT: Record<string, number> = {
  poison: 0x8ac926,
  chill: 0x4cc9f0,
  freeze: 0xa8e6f7,
  shock: 0xfee440,
  stun: 0xffd60a,
  wither: 0x9b5de5,
  mark: 0xff006e,
};

/** Damage kinds carry a colour so an effect says what KIND of hit it was. */
const KIND_TINT = {
  physical: 0xffffff,
  magic: 0x9b5de5,
  true: 0xffd60a,
} as const;

/** Per-enemy white flash, keyed by entity id (R-04-15). */
export interface FlashState {
  enemyId: number;
  remainingMs: number;
}

export class Vfx {
  readonly particles = new ParticleSystem();
  readonly shake = new Screenshake();
  readonly damageNumbers = new DamageNumbers();
  readonly audio: AudioBus;

  /** Full-screen red pulse on core damage (R-04-17). 0–1. */
  vignette = 0;

  /** Consumed and cleared by the loop each frame. */
  pendingHitstopMs = 0;

  /**
   * Where the Core is, for the effects that belong to the RUN rather than to a spot on
   * the board — wave start, wave clear, run end.
   *
   * Handed in by the caller when the world is built, not read off the world here: this
   * class never touches world state (R-04-01), and a wave banner is not worth breaking
   * that for. Defaults to the middle of the map so it is never nowhere.
   */
  private coreX = 16;
  private coreY = 9;

  private readonly flashes = new Map<number, number>();
  /** Towers holding a sustained beam in the batch being handled. Rebuilt per call. */
  private readonly channelling = new Set<number>();
  private settings: VfxSettings = { ...DEFAULT_VFX_SETTINGS };

  /**
   * Resolves a class colour for an effect. Supplied by the caller so this module never
   * imports content — the same reason the renderer takes a lookup.
   */
  tintFor: (defId: string) => number = () => 0xffffff;

  constructor(audio?: AudioBus) {
    this.audio = audio ?? new AudioBus(new SilentSink());
  }

  /** Where run-level effects are staged. Called once per world (see `coreX`). */
  setCore(x: number, y: number): void {
    this.coreX = x;
    this.coreY = y;
  }

  applySettings(settings: Partial<VfxSettings>): void {
    this.settings = { ...this.settings, ...settings };
    this.shake.scale = this.settings.screenshakeScale;
    this.damageNumbers.mode = this.settings.damageNumbers;
  }

  get currentSettings(): VfxSettings {
    return { ...this.settings };
  }

  /**
   * React to one tick's events.
   *
   * @param speed Current sim speed. Only used to suppress hitstop at 4× (R-04-13);
   *              particle rates are deliberately NOT scaled by it.
   */
  handleEvents(events: readonly SimEvent[], speed: number): void {
    // A sustained laser resolves ten times a second. Giving each of those a muzzle flash
    // and a shot sound buries the tower in particles and drowns out the impacts that are
    // supposed to punctuate (R-04-10) — so channelling towers are collected first and
    // their per-tick `towerFired` is muted. The renderer's held line carries the read.
    this.channelling.clear();
    for (const ev of events) {
      if (ev.t === 'beamFired' && ev.weapon === 'laser') this.channelling.add(ev.tower);
    }

    for (const ev of events) {
      switch (ev.t) {
        case 'towerFired': {
          if (this.channelling.has(ev.tower)) break;
          // Aimed down the barrel. The event carries the target, so the flash can point
          // where the shot went instead of blooming symmetrically — which is most of what
          // makes a row of towers read as firing rather than as blinking.
          const aim = Math.atan2(ev.targetY - ev.y, ev.targetX - ev.x);
          this.particles.emit('muzzle', ev.x, ev.y, {
            direction: aim,
            sourceTint: 0xffffff,
          });
          this.particles.emit('muzzleSmoke', ev.x, ev.y, { direction: aim });
          this.audio.play(ev.kind === 'magic' ? 'shoot_magic' : 'shoot_physical', 0.35);
          // No trauma. Forty towers firing would hold the screen in permanent motion and
          // drown out the impacts that should actually punctuate (R-04-10).
          break;
        }

        case 'beamFired': {
          // The renderer owns the line; these are the motes coming off it. Sampled along
          // its length so a beam looks like it is burning the air rather than being a
          // drawn rectangle.
          const tint = KIND_TINT[ev.kind];
          const samples = ev.weapon === 'laser' ? 2 : 4;
          for (let i = 0; i < samples; i++) {
            const t = (i + 0.5) / samples;
            this.particles.emit(
              'beamMote',
              ev.x0 + (ev.x1 - ev.x0) * t,
              ev.y0 + (ev.y1 - ev.y0) * t,
              { sourceTint: tint, spread: 0.12 },
            );
          }
          if (ev.chainHop === 0 && ev.weapon !== 'laser') {
            this.particles.emit('muzzle', ev.x0, ev.y0, {
              direction: Math.atan2(ev.y1 - ev.y0, ev.x1 - ev.x0),
              sourceTint: tint,
            });
          }
          // The trigger pull, once per volley. A chain's hops are the SAME shot arcing
          // on, so hop 0 is the only one that makes a noise — otherwise a Tempest-4
          // board plays three sounds per shot and turns into a buzz.
          //
          // Laser is excluded on purpose: at a 0.1s attack interval it is a continuous
          // weapon, and ten one-shots a second is a machine gun, not a beam. It wants a
          // sustained loop keyed to the channel, which is its own piece of work.
          if (ev.chainHop === 0 && ev.weapon !== 'laser') {
            this.audio.play(
              ev.weapon === 'chain'
                ? 'shoot_chain'
                : ev.kind === 'magic'
                  ? 'shoot_magic'
                  : 'shoot_physical',
              0.35,
            );
          }
          break;
        }

        case 'projectileHit': {
          const tint = KIND_TINT[ev.kind];

          // A blast is drawn AT THE RADIUS IT HAD. Everything scales off `aoeRadius`:
          // how wide the debris scatters, how far the fire throws, how big the flash
          // opens. A mortar and a grenade should not produce the same picture.
          if (ev.aoeRadius > 0) {
            const r = ev.aoeRadius;
            this.particles.emit('explosionFlash', ev.x, ev.y, { sizeScale: r * 0.85 });
            this.particles.emit('explosionFire', ev.x, ev.y, {
              spread: r * 0.35,
              speedScale: Math.min(2.2, 0.7 + r * 0.5),
              countScale: Math.min(2, 0.8 + r * 0.35),
            });
            this.particles.emit('explosionSmoke', ev.x, ev.y, { spread: r * 0.5 });
            this.particles.emit('ember', ev.x, ev.y, {
              spread: r * 0.4,
              countScale: Math.min(2, 0.7 + r * 0.4),
            });
            this.shake.add(ev.crit ? TRAUMA.crit : TRAUMA.explosion);
            if (ev.crit) this.requestHitstop(HITSTOP.crit, speed);
            // An area hit is an explosion whether or not it crit — the crit is already
            // being said by the extra trauma and the hitstop, and routing crits to a
            // different sample meant a critical blast was the one blast you did not hear.
            this.audio.play('explosion', ev.crit ? 0.95 : 0.75);
            break;
          }

          // A pass-through. The shot is still on screen and still moving, so the effect
          // has to say "through", not "stopped": a hard spray thrown along the line of
          // travel and a bright hole where it went in.
          if (ev.pierced) {
            this.particles.emit('pierceSpray', ev.x, ev.y, {
              direction: Math.atan2(ev.y - ev.fromY, ev.x - ev.fromX),
            });
            this.particles.emit('pierceHole', ev.x, ev.y);
            this.shake.add(TRAUMA.hit);
            this.audio.play('impact', 0.45);
            break;
          }

          // A laser tick and a chain hop land ten times a second. Full impact treatment —
          // shockwave, trauma, hitstop — turns a sustained beam into an unplayable strobe,
          // so the small sustained hits get particles only.
          if (ev.chainHop > 0) {
            this.particles.emit('impact', ev.x, ev.y, { sourceTint: tint });
            this.particles.emit('chainArc', ev.x, ev.y);
            break;
          }
          if (ev.crit) {
            this.particles.emit('impactCrit', ev.x, ev.y, { sourceTint: tint });
            this.particles.emit('shockwave', ev.x, ev.y);
            this.particles.emit('ember', ev.x, ev.y, { countScale: 0.5 });
            this.shake.add(TRAUMA.crit);
            this.requestHitstop(HITSTOP.crit, speed);
            this.audio.play('crit');
          } else {
            this.particles.emit('impact', ev.x, ev.y, { sourceTint: tint });
            this.particles.emit('muzzle', ev.x, ev.y, {
              direction: Math.atan2(ev.y - ev.fromY, ev.x - ev.fromX) + Math.PI,
              sourceTint: tint,
              countScale: 0.6,
            });
            this.shake.add(TRAUMA.hit);
            this.audio.play('impact', 0.5);
          }
          break;
        }

        // B-18/B-19 — "the area effect make small particle come up from it". Motes drift
        // up off the WHOLE footprint, scattered across the real radius, so a zone reads as
        // ground doing something rather than as a decal with a ring around it.
        case 'zoneStruck': {
          const tint = KIND_TINT[ev.kind];
          this.particles.emit('zoneStrike', ev.x, ev.y, {
            sourceTint: tint,
            spread: ev.radius * 0.75,
            countScale: Math.min(2, 0.7 + ev.radius * 0.3),
          });
          this.particles.emit('zoneMote', ev.x, ev.y, {
            sourceTint: tint,
            spread: ev.radius,
            countScale: Math.min(2, 0.7 + ev.radius * 0.3),
          });
          this.particles.emit('explosionFlash', ev.x, ev.y, {
            sizeScale: ev.radius * 0.5,
            countScale: 1,
          });
          this.shake.add(TRAUMA.hit);
          this.audio.play('impact', 0.4);
          break;
        }

        case 'enemyDamaged': {
          this.damageNumbers.push(ev.enemy, ev.x, ev.y, ev.amount, ev.kind, ev.crit);

          // A hit turned by the frontal plate has to LOOK turned, or a player reading a
          // small number just concludes their tower is weak. Sparks off steel instead of
          // a wound, and no blood puff at all — three quarters of that shot did not land.
          if (ev.deflected) {
            this.particles.emit('impactCrit', ev.x, ev.y, {
              sourceTint: 0xc9d6e4,
              countScale: 1.6,
            });
            this.particles.emit('shockwave', ev.x, ev.y, { sizeScale: 0.5 });
            this.audio.play('ui_click', 0.25);
            if (this.settings.showDamageFlash) this.flashes.set(ev.enemy, 60);
            break;
          }
          // A hit that rang off a shield does not draw blood. Same puff, cold tint — the
          // player has to be able to tell "chipping the bar" from "hurting it" without
          // reading the numbers, because those two are entirely different jobs.
          this.particles.emit('bloodPuff', ev.x, ev.y, {
            sourceTint: ev.shielded ? 0x4fd8ff : 0xc8503c,
          });
          // Every single point of damage puts something on screen, including the sourceless
          // kind — a poison tick with no flecks is a health bar moving on its own.
          this.particles.emit('statusPuff', ev.x, ev.y, {
            sourceTint: KIND_TINT[ev.kind],
            countScale: 0.4,
          });
          if (this.settings.showDamageFlash) this.flashes.set(ev.enemy, 60);
          break;
        }

        case 'enemyKilled': {
          if (ev.isBoss) {
            this.particles.emit('deathBoss', ev.x, ev.y);
            this.particles.emit('shockwave', ev.x, ev.y, { countScale: 3 });
            this.particles.emit('explosionFlash', ev.x, ev.y, { sizeScale: 2.2 });
            this.particles.emit('explosionFire', ev.x, ev.y, { spread: 0.6, countScale: 1.6 });
            this.particles.emit('explosionSmoke', ev.x, ev.y, { spread: 0.9, countScale: 1.8 });
            this.particles.emit('ember', ev.x, ev.y, { spread: 0.7, countScale: 2.5 });
            this.shake.add(TRAUMA.bossKill);
            this.requestHitstop(HITSTOP.bossKill, speed);
            this.audio.play('boss_die');
          } else {
            this.particles.emit('death', ev.x, ev.y, { sourceTint: 0xc8503c });
            this.particles.emit('shockwave', ev.x, ev.y, { sizeScale: 0.5 });
            this.particles.emit('ember', ev.x, ev.y, { countScale: 0.6 });
            this.shake.add(TRAUMA.kill);
            this.audio.play('enemy_die_small', 0.6);
          }
          // The bounty, popping off the corpse. Gold is the thing the player is actually
          // farming and it had no effect at all — `goldPop` existed and was never emitted,
          // because `goldChanged` has no position to emit it at. A kill does.
          this.particles.emit('goldPop', ev.x, ev.y, { countScale: ev.isBoss ? 4 : 1 });
          this.flashes.delete(ev.enemy);
          break;
        }

        case 'statusApplied': {
          // Was a single particle emitted at (0, 0) — the top-left corner of the map —
          // because the event had no position and `countScale: 0` floored to one. Every
          // status now puffs on the enemy, in its own colour.
          const tint = STATUS_TINT[ev.status] ?? 0xffffff;
          if (ev.status === 'freeze') {
            this.particles.emit('freeze', ev.x, ev.y, { countScale: 0.35 });
          } else {
            this.particles.emit('statusPuff', ev.x, ev.y, {
              sourceTint: tint,
              countScale: Math.min(2, 0.7 + ev.stacks * 0.2),
            });
          }
          if (ev.status === 'poison') {
            this.particles.emit('poisonTick', ev.x, ev.y, { countScale: 3 });
          }
          break;
        }

        case 'enemyLeaped': {
          // The landing thud (B-16): dust and a touch of shake where the leaper comes
          // down, so the hop reads as weight rather than as a position glitch. The
          // take-off gets its own kick-up, or the arc starts from nothing.
          this.particles.emit('emergeDirt', ev.x0, ev.y0, { countScale: 0.7 });
          this.particles.emit('shockwave', ev.x1, ev.y1);
          this.particles.emit('impact', ev.x1, ev.y1, { sourceTint: 0xc8503c });
          this.particles.emit('emergeDirt', ev.x1, ev.y1);
          this.shake.add(TRAUMA.hit);
          break;
        }

        // Prism's blink. Both ends get an effect: shards thrown off where it left, and a
        // hard collapse where it lands, so the jump reads as deliberate rather than as
        // the boss teleporting because the renderer lost it.
        case 'enemyTeleported': {
          this.particles.emit('teleportOut', ev.x0, ev.y0);
          this.particles.emit('shockwave', ev.x0, ev.y0, { sizeScale: 0.7 });
          this.particles.emit('teleportIn', ev.x1, ev.y1);
          this.particles.emit('explosionFlash', ev.x1, ev.y1, { sizeScale: 0.8 });
          this.shake.add(TRAUMA.hit);
          break;
        }

        // Rising out of the ground. Emitted once, on the tick the entrance starts — the
        // renderer animates the body, this is the earth it comes through.
        case 'enemyEmerging': {
          this.particles.emit('emergeDirt', ev.x, ev.y, { countScale: 3, spread: 0.4 });
          this.particles.emit('explosionSmoke', ev.x, ev.y, { spread: 0.5, countScale: 0.8 });
          this.particles.emit('shockwave', ev.x, ev.y, { sizeScale: 0.9 });
          this.shake.add(TRAUMA.explosion);
          break;
        }

        // The shield coming apart. Loud on purpose: it is the cue to dump everything, and
        // a player who misses it loses the only window the fight offers.
        case 'enemyShieldBroken': {
          this.particles.emit('explosionFlash', ev.x, ev.y, { sourceTint: 0x9a7bff });
          this.particles.emit('pierceSpray', ev.x, ev.y, {
            sourceTint: 0x4fd8ff,
            countScale: 4,
            spread: 0.9,
          });
          this.particles.emit('shockwave', ev.x, ev.y, { sizeScale: 1.6, countScale: 2 });
          this.shake.add(TRAUMA.synergyTier6);
          break;
        }

        // ...and going back up, which is the cue to STOP holding a burst.
        case 'enemyShieldRestored': {
          this.particles.emit('teleportIn', ev.x, ev.y, { sourceTint: 0x4fd8ff });
          this.particles.emit('shockwave', ev.x, ev.y, { sizeScale: 1.1 });
          this.shake.add(TRAUMA.hit);
          break;
        }

        // The Disabler's shot. Loud on purpose: the reach that produced it is invisible,
        // so if the strike itself is quiet a tower simply goes dark for no reason a player
        // can see, and that reads as a bug rather than as an attack.
        case 'towersDisabled': {
          const HEX = 0x9b5de5;
          // The line, sampled along its length so it reads as a beam being drawn rather
          // than as a rectangle appearing.
          for (let i = 0; i <= 8; i++) {
            const t = i / 8;
            this.particles.emit('beamMote', ev.x0 + (ev.x - ev.x0) * t, ev.y0 + (ev.y - ev.y0) * t, {
              sourceTint: HEX,
              spread: 0.14,
            });
          }
          // The blast, sized to the radius the sim actually used — the renderer must not
          // guess at a number the simulation owns.
          this.particles.emit('explosionFlash', ev.x, ev.y, {
            sourceTint: HEX,
            sizeScale: ev.radius,
          });
          this.particles.emit('shockwave', ev.x, ev.y, { sizeScale: ev.radius * 1.2, countScale: 2 });
          this.particles.emit('statusPuff', ev.x, ev.y, {
            sourceTint: HEX,
            spread: ev.radius,
            countScale: 3,
          });
          this.shake.add(ev.towers.length > 0 ? TRAUMA.synergyTier6 : TRAUMA.hit);
          this.audio.play(ev.towers.length > 0 ? 'synergy_proc' : 'ui_click', 0.5);
          break;
        }

        // The one thing the player DOES beyond building, so it has to land like a
        // decision. Quieter when ARMED between rounds: nothing is happening yet, and a
        // full detonation over an empty board would promise an effect that has not
        // started.
        case 'supportSurged': {
          const tint = this.tintFor(ev.defId);
          const scale = ev.armed ? 1 : 3;
          this.particles.emit('synergyProc', ev.x, ev.y, { sourceTint: tint, countScale: scale });
          this.particles.emit('shockwave', ev.x, ev.y, {
            sizeScale: ev.armed ? 1.1 : 1.8,
            countScale: ev.armed ? 1 : 2,
          });
          if (!ev.armed) {
            this.particles.emit('beamMote', ev.x, ev.y, {
              sourceTint: tint,
              spread: 0.8,
              countScale: 2,
            });
          }
          this.shake.add(ev.armed ? TRAUMA.hit : TRAUMA.synergyTier6);
          this.audio.play('synergy_proc', ev.armed ? 0.3 : 0.6);
          break;
        }

        case 'synergyProc': {
          this.particles.emit('synergyProc', ev.x, ev.y, { sourceTint: 0xffffff });
          this.particles.emit('shockwave', ev.x, ev.y);
          if (ev.tier === 6) {
            this.shake.add(TRAUMA.synergyTier6);
            this.requestHitstop(HITSTOP.synergyTier6, speed);
          }
          this.audio.play('synergy_proc');
          break;
        }

        case 'towerPlaced': {
          this.particles.emit('placeTower', ev.tileX + 0.5, ev.tileY + 0.5, {
            sourceTint: this.tintFor(ev.defId),
          });
          this.particles.emit('shockwave', ev.tileX + 0.5, ev.tileY + 0.5, { sizeScale: 0.6 });
          this.particles.emit('emergeDirt', ev.tileX + 0.5, ev.tileY + 0.5, { countScale: 0.8 });
          this.audio.play('place_tower');
          break;
        }

        case 'towersMerged': {
          this.particles.emit('synergyProc', ev.x, ev.y, {
            sourceTint: this.tintFor(ev.defId),
            countScale: 2,
          });
          this.particles.emit('shockwave', ev.x, ev.y);
          // Merging is the best feeling in an autobattler and it should look like it.
          this.particles.emit('explosionFlash', ev.x, ev.y, { sizeScale: 1.2 });
          this.particles.emit('zoneMote', ev.x, ev.y, {
            sourceTint: this.tintFor(ev.defId),
            spread: 0.6,
            countScale: 1.5,
          });
          this.particles.emit('ember', ev.x, ev.y, { countScale: 1.5 });
          this.audio.play('merge');
          break;
        }

        case 'towerSold': {
          // Had a sound and nothing to look at, so a sale registered in the ears and not
          // the eyes — on a board where the tower simply vanishes, that reads as a bug.
          this.particles.emit('sellPuff', ev.tileX + 0.5, ev.tileY + 0.5);
          this.particles.emit('goldPop', ev.tileX + 0.5, ev.tileY + 0.5, { countScale: 2 });
          this.audio.play('sell_tower');
          break;
        }

        case 'coreDamaged': {
          // Same origin bug as `statusApplied`: one particle in the map's corner, nowhere
          // near the Core. The event carries the Core's position now.
          this.particles.emit('coreHit', ev.x, ev.y, { spread: 0.3 });
          this.particles.emit('explosionFlash', ev.x, ev.y, { sizeScale: 1.6 });
          this.particles.emit('explosionSmoke', ev.x, ev.y, { spread: 0.5 });
          this.particles.emit('ember', ev.x, ev.y, { spread: 0.4, countScale: 1.5 });
          this.shake.add(TRAUMA.coreDamaged);
          this.requestHitstop(HITSTOP.coreDamaged, speed);
          this.vignette = 1;
          this.audio.play('core_hit');
          break;
        }

        // A buff tile mending the Core. Deliberately unlike `coreDamaged`: rising motes
        // and no shake, because the one moment in a run that gives something back should
        // not share a language with the moment that takes it away.
        case 'coreMended': {
          this.particles.emit('zoneMote', ev.x, ev.y, {
            sourceTint: 0x6fe08a,
            spread: 0.5,
            countScale: 1.5,
          });
          this.particles.emit('goldPop', ev.x, ev.y, { countScale: 1.5 });
          this.audio.play('synergy_proc', 0.5);
          break;
        }

        case 'goldChanged': {
          // Audio only, deliberately: gold changes for reasons that happen in the SHOP,
          // which is DOM and not the board, so a burst on the battlefield would point at
          // the wrong place. The two gold events that do have a location — a bounty and a
          // sale — emit coins from `enemyKilled` and `towerSold` instead.
          if (ev.reason === 'purchase') this.audio.play('buy', 0.7);
          else if (ev.reason === 'reroll') this.audio.play('reroll', 0.7);
          break;
        }

        // The three moments that punctuate a run had sound and no picture. They are
        // emitted at the Core, the one point on the board that is always meaningful and
        // always on screen — `spread` turns each into a burst across the whole area
        // rather than a dot.
        case 'waveStarted': {
          this.particles.emit('waveStart', this.coreX, this.coreY, { spread: 1.2 });
          this.audio.play('wave_start');
          break;
        }

        case 'waveCleared': {
          this.particles.emit('waveClear', this.coreX, this.coreY, { spread: 1.4 });
          this.particles.emit('shockwave', this.coreX, this.coreY, { sizeScale: 2 });
          this.shake.add(TRAUMA.waveCleared);
          this.audio.play('wave_clear');
          break;
        }

        case 'runEnded': {
          this.particles.emit('runEnd', this.coreX, this.coreY, { spread: 3 });
          this.audio.play('run_over');
          break;
        }
      }
    }
  }

  private requestHitstop(ms: number, speed: number): void {
    // Disabled entirely at 4×, or a dense wave stutters permanently (R-04-13).
    if (!this.settings.hitstopEnabled || speed >= 4) return;
    this.pendingHitstopMs = Math.max(this.pendingHitstopMs, ms);
  }

  /** Drain the accumulated freeze request. The loop owns the actual budget cap. */
  takeHitstop(): number {
    const ms = this.pendingHitstopMs;
    this.pendingHitstopMs = 0;
    return ms;
  }

  /** Real elapsed time, never tick-locked (R-04-02). */
  update(deltaMs: number): void {
    this.particles.update(deltaMs);
    this.damageNumbers.update(deltaMs);
    this.shake.update(deltaMs);
    this.audio.update(deltaMs);

    if (this.vignette > 0) this.vignette = Math.max(0, this.vignette - deltaMs / 400);

    for (const [id, remaining] of this.flashes) {
      const next = remaining - deltaMs;
      if (next <= 0) this.flashes.delete(id);
      else this.flashes.set(id, next);
    }
  }

  isFlashing(enemyId: number): boolean {
    return this.flashes.has(enemyId);
  }

  /**
   * The ambient shimmer a buff tile gives off, and that its occupant carries.
   *
   * Takes the preset by NAME because the choice is authored content — each tile kind
   * picks the particle that reads as what it does — and this module must not know what
   * kinds exist. An unknown name is ignored rather than thrown on: a tile whose preset
   * was renamed should lose its sparkle, not the run.
   */
  emitTilePresence(preset: string, x: number, y: number, tint: number): void {
    if (!(preset in PRESETS)) return;
    this.particles.emit(preset as PresetName, x, y, {
      sourceTint: tint,
      countScale: 0.7,
      spread: 0.35,
    });
  }

  /** Trail emission for a moving projectile (R-04-22). */
  emitTrail(x: number, y: number, tint: number): void {
    this.particles.emit('trail', x, y, { sourceTint: tint });
  }

  clear(): void {
    this.particles.clear();
    this.damageNumbers.clear();
    this.shake.reset();
    this.flashes.clear();
    this.vignette = 0;
    this.pendingHitstopMs = 0;
  }
}
